"use strict";

/**
 * JS fuer die Modals (popups) auf der KWS
 */

import {isBlank, isEmpty, isFunction, isNotEmpty, isObject, isString} from "../../util/type-utils.js";
import {closest, find} from "@21torr/dune/dom/traverse.js";
import {mount} from "@21torr/dune/mount.js";
import {attachTouchClickEventListener} from "../../util/event-utils.js";
import anime from "animejs";
import * as _ from 'underscore';
import {initModalComponents} from "./modal-updater.js";
import {
    ANIM_ELEMENT_SELECTOR,
    resetAnimationsforElements
} from "../..//micro-animation.js";
import {disableBodyScroll, enableBodyScroll} from "../../util/scroll-utils.js";
import UrlParameterHandler from "../UrlParameterHandler.js";


export const MODAL_CONTAINER_SELECTOR = '.modal[data-modal-id]';
export const MODAL_LINK_SELECTOR = '.link-modal[data-modal-id]';
export const MODAL_CLOSE_BUTTON_SELECTOR = MODAL_CONTAINER_SELECTOR + ' .modal-close';

export const MODAL_CLOSE_EVENT = 'modal:close';
export const MODAL_OPEN_EVENT = 'modal:open';

const MODAL_OPEN_CLASS = 'is-open';


/**
 * event listener for keydown event
 * Closes the active modal, if the escape key is pressed
 * @param event
 */
const keyboardCloseModalListener = (event) => {
    if (event.isComposing || event.code !== 'Escape') {
        return;
    }

    closeOpenModal();

    event.stopPropagation();
};


/**
 * Oeffnet das Modal mit der uebergebenen ID.
 * Schliesst alle anderen modals wenn closeOthers=true, ausser wenn das andere Modal ein Container des Modals ist
 *
 * @param {String} modalId ID des Modals, das geoeffnet werden soll
 * @param {Boolean} [closeOthers=true] Wenn True, werden alle anderen  Modals zuerst geschlossen. (Ausnahme: Das neue Modal ist ein Kind eines geoeffneten Modals)
 * @param {Boolean} [pushState=true] Wenn True, wird ein neuer Eintrag in der Browser-History angelegt (History API).
 */
export function openModal(modalId, closeOthers = true, pushState = true) {
    let modalToOpen = document.querySelector(`${MODAL_CONTAINER_SELECTOR}[data-modal-id="${modalId}"]`),
        bodyElement = document.querySelector('body');

    if (isEmpty(modalToOpen) || modalToOpen.classList.contains(MODAL_OPEN_CLASS)) {
        return;
    }


    find(MODAL_CONTAINER_SELECTOR).forEach(modal => {
        if (!modal.contains(modalToOpen)) {
            closeModal(modal);
        }
    });

    if (pushState) {
        UrlParameterHandler.setURLHashValue({name: "mod", value: modalId}, 0, true);
    }

    bodyElement.setAttribute('data-modal-open', modalId);

    modalToOpen.style.opacity = "0";
    modalToOpen.classList.add(MODAL_OPEN_CLASS);

    initModalComponents(modalToOpen);

    anime({
        targets: modalToOpen,
        easing: 'easeInOutCubic',
        duration: 400,
        opacity: 1,
    });
    disableBodyScroll();
    modalToOpen.dispatchEvent(new CustomEvent(MODAL_OPEN_EVENT));
}

/**
 * Schliesst das uebergebene Modal
 *
 * @param {String|HTMLElement} modal Modal das, geschlossen werden soll. Kann entweder die Modal-ID oder dsa Modal selbst (HTMLElement) sein.
 * @param {Boolean} [popState=true] Wenn true, wird der State angepasst/gepoppt. Wenn false, wird nur die URL angepasst.
 * @param {Boolean} [doAnimation=true] Wenn true, wird das Schliessen animiert
 * @param {Function} [onClose] Callback wird ausgefuehrt, wenn das Modal geschlossen wurde
 */
export function closeModal(modal, popState = true, doAnimation = true, closeCallback) {
    let modalToClose,
        modalId,
        bodyElement = document.querySelector('body');

    if (isString(modal)) {
        modalToClose = document.querySelector(`${MODAL_CONTAINER_SELECTOR}[data-modal-id="${modal}"]`);
        modalId = modal;
    } else if (isObject(modal) && modal.matches(MODAL_CONTAINER_SELECTOR)) {
        modalToClose = modal;
        modalId = modalToClose.dataset.modalId;
    }

    if (isEmpty(modalToClose) || !modalToClose.classList.contains(MODAL_OPEN_CLASS)) {
        return;
    }

    bodyElement.removeAttribute('data-modal-open');

    if (popState && isNotEmpty(window.history.state) && window.history.state.toString().includes('mod/')) {
        window.history.back();
    } else {
        UrlParameterHandler.setURLHashValue(null, null, false)
    }

    if (doAnimation) {
        anime({
            targets: modal,
            easing: 'easeInOutCubic',
            duration: 400,
            opacity: 0,
            complete() {
                enableBodyScroll();
                modalToClose.classList.remove(MODAL_OPEN_CLASS);
                modalToClose.style.opacity = "";
                reattachMicroAnimationsToChildElements(modalToClose);

                if (isFunction(closeCallback)) {
                    closeCallback(modalToClose);
                }
                modalToClose.dispatchEvent(new CustomEvent(MODAL_CLOSE_EVENT));
            }
        });
    } else {
        enableBodyScroll();
        modalToClose.classList.remove(MODAL_OPEN_CLASS);
        modalToClose.style.opacity = "";

        if (isFunction(closeCallback)) {
            closeCallback(modal);
        }
        modalToClose.dispatchEvent(new CustomEvent(MODAL_CLOSE_EVENT));
    }
}

function reattachMicroAnimationsToChildElements(modal) {
    if (isEmpty(modal)) {
        return;
    }

    modal.querySelectorAll(ANIM_ELEMENT_SELECTOR).forEach(element => resetAnimationsforElements(element));
}


function closeOpenModal() {
    const bodyElement = document.querySelector('body'),
        openModalId = bodyElement.dataset.modalOpen;

    if (isEmpty(openModalId)) {
        return;
    }

    closeModal(openModalId);
}


/**
 * Event-Handler fuer Close-Button des Modals
 */
mount(MODAL_CLOSE_BUTTON_SELECTOR, closeButton => {
    let modal = closest(closeButton, MODAL_CONTAINER_SELECTOR);

    if (isEmpty(modal)) {
        return;
    }

    attachTouchClickEventListener(closeButton, (e) => {
        closeModal(modal);
    })
});


/**
 * Event-Listener fuer Modal-Links
 */
mount(MODAL_LINK_SELECTOR, openButton => {
    if (!isObject(openButton) || !('dataset' in openButton)) {
        return;
    }
    let modalId = openButton.dataset.modalId;

    if (isBlank(modalId)) {
        return;
    }

    attachTouchClickEventListener(openButton, (e) => {
        openModal(modalId);
    });
});


// attach event listener to body, closes modal if modal is open and escape key is pressed
document.querySelector('body').addEventListener('keydown', _.debounce(keyboardCloseModalListener, 500, true));


UrlParameterHandler.subscribe("mod", hashParameterObject => {
    const modalId = hashParameterObject.value;

    if (isNotEmpty(modalId)) {
        openModal(modalId, false, false); // Keinen State pushen, da ansonsten der Zurueck-button auf die aktuelle Seite verweist und nicht auf die Herkunfsseite
    } else {
        closeOpenModal();
    }

});

